import { Repeat } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  withTooltip,
} from '@crossnokaye/ux-common';

import { Button } from '@/ux-common/components/button/button';
import { formatDate } from '@/ux-common/components/calendar';
import { CalendarPicker } from '@/ux-common/components/calendar-picker/calendar-picker';
import { DropdownButton } from '@/ux-common/components/dropdown-button/dropdown-button';

import { DefrostScheduleBaseCellProps } from './defrost-schedule.types';
import { DefrostTableCell } from './defrost-table-cell';

const IconWithTooltip = withTooltip(Icon);

const toStartOfDay = (date: Date): Date => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

export const DefrostScheduleDateCell: React.FC<DefrostScheduleBaseCellProps> = ({ index }) => {
  const { control, setValue } = useFormContext();
  const fieldName = `defrostSchedule.${index}.endDate` as const;
  const todayStartOfDay = useMemo(() => toStartOfDay(new Date()), []);
  const [open, setOpen] = useState(false);

  return (
    <DefrostTableCell>
      {(disabledByPermissions) => (
        <FormField
          control={control}
          name={fieldName}
          render={({ field }) => {
            const commitEndDate = (value: Date | null) => {
              setValue(fieldName, value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              });
              setOpen(false);
            };

            return (
              <FormItem className="flex flex-row gap-2">
                <FormControl>
                  <div className="flex min-w-0 flex-1 gap-2">
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger asChild>
                        <DropdownButton
                          data-testid="defrost-schedule-end-date-picker"
                          disabled={disabledByPermissions}
                          className="btn-tier3 min-w-0 flex-1 justify-start"
                        >
                          {field.value
                            ? formatDate(field.value, { format: 'medium' })
                            : 'Repeat Forever'}
                        </DropdownButton>
                      </PopoverTrigger>
                      <PopoverContent className="z-overlay w-auto p-0" withPortal>
                        <CalendarPicker
                          mode="single"
                          defaultValue={field.value ?? undefined}
                          onConfirm={(selectedDate: Date) => {
                            commitEndDate(selectedDate ?? null);
                          }}
                          onCancel={() => setOpen(false)}
                          disabled={(date: Date) => toStartOfDay(date) <= todayStartOfDay}
                        />
                      </PopoverContent>
                    </Popover>
                    <Button
                      aria-label="Repeat forever"
                      className="btn btn-tier3 btn-square shrink-0"
                      data-testid="defrost-schedule-repeat-forever"
                      disabled={disabledByPermissions}
                      onClick={() => {
                        commitEndDate(null);
                      }}
                    >
                      <IconWithTooltip
                        icon={Repeat}
                        variant="square"
                        className="btn-icon bg-transparent"
                        tooltipContent="Repeat Forever"
                      />
                    </Button>
                  </div>
                </FormControl>
              </FormItem>
            );
          }}
        />
      )}
    </DefrostTableCell>
  );
};
